/**
 * ContactSurfaceContext - the contact page's binding of the shared
 * detail-surface context.
 *
 * The machinery (an optional context, a typed API) is in
 * `shared/surface/DetailSurfaceContext`. What is particular to contacts is two
 * types: which pane ids exist, and what the reader pane can be asked to show.
 *
 * NO SELECTION. The page edits the contact inline on the Details tab and never
 * selects a row into a shared detail container, so it takes
 * `DetailSurfaceApiWithoutSelection` - panes, the reader and tabs, and no
 * `select` that would open a pane it never declared.
 *
 * IT IS OPTIONAL ON PURPOSE. `useContactSurface()` returns null outside the
 * provider, so a component rendered anywhere else keeps working: the call
 * sites fall back to whatever they did before the surface existed.
 */

import {
  DetailSurfaceApiWithoutSelection,
  createDetailSurfaceContext,
} from '../../shared/surface/DetailSurfaceContext';
import { ContactPanelId } from './layout';

/**
 * What the reader pane is showing.
 *
 * One shape, because a contact has exactly one kind of readable thing: an
 * uploaded document, fetched as a blob through `useGlobalStreaming` and handed
 * to the same `ReaderComponent` every other surface reads with.
 */
export interface ContactReaderContent {
  /** Header line for the pane. */
  title: string;
  /** Still streaming - the pane shows a loader under the title. */
  loading?: boolean;
  citation?: any;
  /** The contact the document hangs off, for the reader's own actions. */
  clientUuid: string;
  userAccessLevel?: 'edit' | 'admin' | 'view';
}

export type ContactSurfaceApi = DetailSurfaceApiWithoutSelection<
  ContactPanelId,
  ContactReaderContent
>;

const binding = createDetailSurfaceContext<ContactSurfaceApi>();

export const ContactSurfaceProvider = binding.Provider;

/** Null outside a contact surface - callers fall back to their old behaviour. */
export const useContactSurface = binding.useSurface;

export default binding.Context;
